import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { appConfig } from '../config/appConfig';
import { letterFormLimits } from '../config/limits';
import { letterService } from '../services/letterService';
import { toUserMessage } from '../utils/errors';
import {
  isValidLetterForm,
  toNewLetter,
  validateLetterForm,
  type LetterFormErrors,
  type LetterFormValues,
} from '../utils/letterValidation';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const EMPTY_FORM: LetterFormValues = { name: '', message: '' };
const { maxNameLength, maxMessageLength } = letterFormLimits;

/**
 * Writing a letter.
 *
 * Deliberately the same shape as the Add a Memory page, because it is the same
 * arrangement: anybody holding the family invitation may write one, and nothing
 * appears until an administrator has read it. What differs is the room for the
 * words and how it is addressed.
 */
export function AddLetterPage() {
  const [values, setValues] = useState<LetterFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<LetterFormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  function updateField(field: keyof LetterFormValues, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    // Clear the message for a field as soon as the writer starts correcting it.
    setErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const validationErrors = validateLetterForm(values);
    setErrors(validationErrors);

    if (!isValidLetterForm(validationErrors)) {
      // Send keyboard and screen reader users straight to the first problem.
      if (validationErrors.name) nameRef.current?.focus();
      else messageRef.current?.focus();
      return;
    }

    setSubmitState('submitting');
    setSubmitError(null);

    try {
      await letterService.addLetter(toNewLetter(values));
      setValues(EMPTY_FORM);
      setSubmitState('success');
    } catch (cause) {
      setSubmitError(toUserMessage(cause, 'Your letter could not be saved. Please try again.'));
      setSubmitState('error');
    }
  }

  const isSubmitting = submitState === 'submitting';

  return (
    <div className="container container--reading">
      <header className="page-header">
        <h1>Write a letter</h1>
        <p>
          Write to him directly, in as many words as you like. Letters appear on the{' '}
          <Link to="/letters">Letters to Dad</Link> page.
        </p>
      </header>

      {submitState === 'success' ? (
        <div className="notice notice--success notice--spaced" role="status">
          <p className="notice__heading">Thank you — your letter has been received.</p>
          <p>
            {appConfig.useMockData
              ? 'This site is not yet connected to its backend, so the letter is kept only until the page is reloaded.'
              : 'It will appear on the Letters to Dad page once it has been checked. That is usually quick, but it will not be there straight away.'}
          </p>
        </div>
      ) : null}

      {submitState === 'error' && submitError ? (
        <div className="notice notice--error notice--spaced" role="alert">
          <p className="notice__heading">Your letter could not be saved</p>
          <p>{submitError}</p>
        </div>
      ) : null}

      <form className="card" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="letter-name">
            Your name
          </label>
          <span className="field__hint" id="letter-name-hint">
            Up to {maxNameLength} characters. Shown underneath your letter, as a signature.
          </span>
          <input
            ref={nameRef}
            id="letter-name"
            name="name"
            type="text"
            className="field__control"
            value={values.name}
            onChange={(event) => updateField('name', event.target.value)}
            autoComplete="name"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={
              errors.name ? 'letter-name-error letter-name-hint' : 'letter-name-hint'
            }
            disabled={isSubmitting}
          />
          {errors.name ? (
            <span className="field__error" id="letter-name-error">
              {errors.name}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="letter-message">
            Your letter
          </label>
          <span className="field__hint" id="letter-message-hint">
            Up to {maxMessageLength.toLocaleString('en-GB')} characters — far more than you are
            likely to need. Blank lines between paragraphs are kept.
          </span>
          <textarea
            ref={messageRef}
            id="letter-message"
            name="message"
            className="field__control"
            value={values.message}
            onChange={(event) => updateField('message', event.target.value)}
            rows={16}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={
              errors.message ? 'letter-message-error letter-message-hint' : 'letter-message-hint'
            }
            disabled={isSubmitting}
          />
          {errors.message ? (
            <span className="field__error" id="letter-message-error">
              {errors.message}
            </span>
          ) : null}
        </div>

        <button type="submit" className="button" disabled={isSubmitting}>
          {isSubmitting ? 'Sending your letter…' : 'Send letter'}
        </button>
      </form>
    </div>
  );
}
